import { feeImageStreamParser, } from '@debbie/common/dist/bespoke/apcoa/index.js';
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import * as api from '../../api/bespoke/apcoa.js';
import { getTenantId, useTenantId } from '../../tenant/selected.js';
const FRESH_TIME = 1000 * 60 * 5;
export const MAX_FEE_IMAGE_CACHE_ENTRIES = 2;
const emptyState = {
    images: [],
    status: 'idle',
};
const entries = new Map();
const feeImagesKey = (tenantId, feeId) => `${tenantId}:apcoa-fee-images:${feeId}`;
const getEntry = (key) => {
    const cached = entries.get(key);
    if (cached) {
        cached.lastAccessedAt = Date.now();
        return cached;
    }
    const entry = {
        lastAccessedAt: Date.now(),
        state: emptyState,
        subscribers: new Set(),
    };
    entries.set(key, entry);
    return entry;
};
const updateEntry = (entry, state) => {
    entry.lastAccessedAt = Date.now();
    entry.state = state;
    for (const subscriber of entry.subscribers) {
        subscriber();
    }
};
const pruneCache = () => {
    if (entries.size <= MAX_FEE_IMAGE_CACHE_ENTRIES) {
        return;
    }
    const unusedEntries = [...entries.entries()]
        .filter(([, entry]) => !entry.inFlight && entry.subscribers.size === 0)
        .sort(([, a], [, b]) => a.lastAccessedAt - b.lastAccessedAt);
    while (entries.size > MAX_FEE_IMAGE_CACHE_ENTRIES &&
        unusedEntries.length > 0) {
        const [key] = unusedEntries.shift();
        entries.delete(key);
    }
};
const imageKey = (image) => `${image.supervisionFeeNo}:${image.fileName}`;
const mergeImages = (cached, incoming) => {
    const images = new Map(cached.map((image) => [imageKey(image), image]));
    for (const image of incoming) {
        images.set(imageKey(image), image);
    }
    return [...images.values()];
};
const toError = (error) => error instanceof Error ? error : new Error('Error loading fee images');
export const prefetchFeeImages = (feeId, tenantId = getTenantId()) => {
    if (!tenantId) {
        return Promise.resolve();
    }
    const entry = getEntry(feeImagesKey(tenantId, feeId));
    if (entry.inFlight) {
        return entry.inFlight;
    }
    if (entry.state.updatedAt != null &&
        Date.now() - entry.state.updatedAt < FRESH_TIME) {
        return Promise.resolve();
    }
    const cachedImages = entry.state.images;
    updateEntry(entry, {
        images: cachedImages,
        status: cachedImages.length > 0 ? 'refreshing' : 'loading',
    });
    const request = (async () => {
        const incomingImages = [];
        try {
            const body = await api.getFeeImagesStream(feeId, tenantId);
            if (body == null) {
                updateEntry(entry, {
                    images: [],
                    status: 'ready',
                    updatedAt: Date.now(),
                });
                return;
            }
            const decoder = new TextDecoder('utf-8');
            const reader = body.getReader();
            let parserState = { state: 'initial' };
            const parse = (text) => {
                let remaining = text;
                while (remaining !== '') {
                    const [newState, newRemaining, image] = feeImageStreamParser(parserState, remaining);
                    parserState = newState;
                    remaining = newRemaining;
                    if (image != null) {
                        const imageIndex = incomingImages.findIndex((incoming) => imageKey(incoming) === imageKey(image));
                        if (imageIndex === -1) {
                            incomingImages.push(image);
                        }
                        else {
                            incomingImages[imageIndex] = image;
                        }
                        updateEntry(entry, {
                            images: mergeImages(cachedImages, incomingImages),
                            status: cachedImages.length > 0 ? 'refreshing' : 'loading',
                        });
                    }
                }
            };
            while (true) {
                const result = await reader.read();
                if (result.done) {
                    break;
                }
                parse(decoder.decode(result.value, { stream: true }));
            }
            parse(decoder.decode());
            updateEntry(entry, {
                images: incomingImages,
                status: 'ready',
                updatedAt: Date.now(),
            });
        }
        catch (error) {
            updateEntry(entry, {
                images: entry.state.images,
                status: 'error',
                error: toError(error),
                updatedAt: entry.state.updatedAt,
            });
        }
    })().finally(() => {
        entry.inFlight = undefined;
        pruneCache();
    });
    entry.inFlight = request;
    pruneCache();
    return request;
};
export const useFeeImages = (feeId) => {
    const tenantId = useTenantId();
    const key = tenantId && feeId ? feeImagesKey(tenantId, feeId) : null;
    const subscribe = useCallback((subscriber) => {
        if (key == null) {
            return () => { };
        }
        const entry = getEntry(key);
        entry.subscribers.add(subscriber);
        return () => {
            entry.subscribers.delete(subscriber);
            pruneCache();
        };
    }, [key]);
    const getSnapshot = useCallback(() => (key == null ? emptyState : getEntry(key).state), [key]);
    const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    useEffect(() => {
        if (feeId && tenantId) {
            void prefetchFeeImages(feeId, tenantId);
        }
    }, [feeId, tenantId]);
    return state;
};
export const clearFeeImagesCache = () => {
    entries.clear();
};
export const getFeeImagesCacheState = (feeId, tenantId) => getEntry(feeImagesKey(tenantId, feeId)).state;
//# sourceMappingURL=fee-images.js.map